import React, { useEffect, useMemo, useState } from "react";
import { Bar, Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  PluginServiceGlobalRegistrationAndOptions,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PieController,
  ArcElement
} from "chart.js";
import { api } from "../../utils/api";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { PuffLoader } from "react-spinners";
import { redirect } from "react-router-dom";

// Register the required components from Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PieController,
  ArcElement,
  ChartDataLabels
);

const TransactionSalesCharts = ({ dataSource, appliedFilter }) => {
  const [loading, setLoading] = useState(true);
  const currentPortal = sessionStorage.getItem("selectedPortal");
  const selectedCompany = sessionStorage.getItem("selectedCompany");
  const p1Region = process.env.REACT_APP_P1_REGION;
  const targetTimeZone = p1Region === "NZ" ? "Pacific/Auckland" : "Australia/Sydney";

  const [salesData, setSalesData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalValue, setTotalValue] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [paginatedData, setPaginatedData] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  // console.log("appliedFilter", appliedFilter);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (Object.keys(appliedFilter).length === 0 && dataSource === "transactions") {
        return;
      }

      setLoading(true);
      try {
        const endpoint = dataSource === "redemptions" ? "v1/redemption/search" : "v1/transaction/search";
        let query = { query: appliedFilter };

        switch (currentPortal) {
          case "Partner":
            query = { query: { ...appliedFilter, partnerCompany: selectedCompany } };
            break;
          case "Administrator":
            query = { query: appliedFilter };
            break;
          default:
            break;
        }

        if (dataSource === "transactions") {
          query.limit = 10000;
        }

        const response = await api(endpoint, "POST", query);
        if (cancelled) return;
        const sortedData = response.data.data.searchResult.sort(
          (a, b) => new Date(b.createdOnUTC) - new Date(a.createdOnUTC)
        );

        const paidTransactions = sortedData.filter((t) => t.invoiceStatus === "PAID");
        const total = paidTransactions.length;
        const totalPrice = paidTransactions.reduce(
          (sum, transaction) => sum + (parseFloat(transaction.price) || 0),
          0
        );
        setTotalCount(total);
        setTotalValue(totalPrice);
        setSalesData(sortedData);
      } catch (error) {
        console.error("Error fetching sales data:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [appliedFilter, dataSource, currentPortal, selectedCompany]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilter]);

  const paidSalesData = useMemo(() => {
    return salesData.filter((t) => t.invoiceStatus === "PAID");
  }, [salesData]);

  // Partner / product breakdowns use all rows returned for the filter (not PAID-only).
  // Many partner-attributed sales stay non-PAID (pending etc.) while renewals often post as PAID under P1 Internal;
  // restricting these charts to PAID-only hid Denza and other partners.
  const countSalesByCompany = () => {
    const salesByCompany = {};
    salesData.forEach((sale) => {
      const companyName = sale.partnerCompany && sale.partnerCompany.name ? sale.partnerCompany.name : "Unknown";
      if (!salesByCompany[companyName]) {
        salesByCompany[companyName] = { count: 0, totalPrice: 0 };
      }
      salesByCompany[companyName].count += 1;
      salesByCompany[companyName].totalPrice += parseFloat(sale.price) || 0;
    });
    return salesByCompany;
  };

  // Count sales by company and prepare data
  const salesByCompany = countSalesByCompany();
  const salesByCompanyData = {
    labels: Object.keys(salesByCompany),
    datasets: [
      {
        label: "Total Price",
        data: Object.values(salesByCompany).map((company) => company.count),
        backgroundColor: Array.from({ length: 50 }, (_, index) => `hsl(${index * 100}, 50%, 50%)`)
      }
    ]
  };

  // Custom tooltip for sales by company to show both total price and count
  const companyOptions = {
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const companyName = tooltipItem.label;
            const data = salesByCompany[companyName];
            const totalPrice = data.totalPrice || 0; // Default to 0 if totalPrice is not defined
            return [
              `Total Price: $${typeof totalPrice === "number" ? totalPrice.toFixed(2) : totalPrice}`,
              `Count: ${data.count}`
            ];
          }
        }
      }
    }
  };

  const getDayKeyInTimeZone = (dateValue) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (isNaN(date.getTime())) return null;

    // en-CA gives YYYY-MM-DD ordering; we still parse parts to avoid any locale edge cases.
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: targetTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);

    const year = parts.find((p) => p.type === "year")?.value;
    const month = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;

    if (!year || !month || !day) return null;
    return `${year}-${month}-${day}`;
  };

  // Function to count total sales and price by day
  const countTotalSalesByDay = (transactions) => {
    const totalSalesByDay = {};
    transactions.forEach((sale) => {
      const dayKey = getDayKeyInTimeZone(sale.createdOnUTC);

      if (dayKey) {
        if (!totalSalesByDay[dayKey]) {
          totalSalesByDay[dayKey] = { count: 0, totalPrice: 0 };
        }
        totalSalesByDay[dayKey].count += 1;
        totalSalesByDay[dayKey].totalPrice += parseInt(sale.price) || 0;
      } else {
        console.error("Invalid Date:", sale.createdOnUTC);
      }
    });
    return totalSalesByDay;
  };

  // Format date for display
  const formatDateForChart = (dateString) => {
    const [year, month, day] = dateString.split("-");
    const date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
    return date.toLocaleDateString("en-AU", {
      timeZone: targetTimeZone,
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Sort the days in chronological order
  const sortDays = (dayKeys) => {
    return dayKeys.sort((a, b) => {
      // Compare as strings since YYYY-MM-DD format sorts correctly
      return a.localeCompare(b);
    });
  };

  const paidNewSalesData = paidSalesData.filter((t) => t.billingCategory === "new");
  const paidRenewalSalesData = paidSalesData.filter((t) => t.billingCategory === "renewal");

  const buildDayOptions = (salesByDay, sortedDays) => ({
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const dayKey = sortedDays[tooltipItem.dataIndex];
            const data = salesByDay[dayKey];
            const totalPrice = data.totalPrice || 0;
            return [
              `Total Price: $${typeof totalPrice === "number" ? totalPrice.toFixed(2) : totalPrice}`,
              `Count: ${data.count}`,
            ];
          },
        },
      },
    },
  });

  // New subscription creations per day
  const newTotalSalesByDay = countTotalSalesByDay(paidNewSalesData);
  const sortedNewDays = sortDays(Object.keys(newTotalSalesByDay));
  const newTotalSalesByDayData = {
    labels: sortedNewDays.map((dayKey) => formatDateForChart(dayKey)),
    datasets: [
      {
        label: "New Subscriptions",
        data: sortedNewDays.map((dayKey) => newTotalSalesByDay[dayKey].count),
        backgroundColor: "rgba(153, 102, 255, 0.6)",
      },
    ],
  };
  const newDayOptions = buildDayOptions(newTotalSalesByDay, sortedNewDays);

  // Renewal (recurring) payments per day
  const renewalTotalSalesByDay = countTotalSalesByDay(paidRenewalSalesData);
  const sortedRenewalDays = sortDays(Object.keys(renewalTotalSalesByDay));
  const renewalTotalSalesByDayData = {
    labels: sortedRenewalDays.map((dayKey) => formatDateForChart(dayKey)),
    datasets: [
      {
        label: "Renewals",
        data: sortedRenewalDays.map((dayKey) => renewalTotalSalesByDay[dayKey].count),
        backgroundColor: "rgba(54, 162, 235, 0.6)",
      },
    ],
  };
  const renewalDayOptions = buildDayOptions(renewalTotalSalesByDay, sortedRenewalDays);

  const countSalesByProduct = () => {
    const salesByProduct = {};
    salesData.forEach((sale) => {
      const productName = sale.product && sale.product.name ? sale.product.name : "Unknown";
      if (!salesByProduct[productName]) {
        salesByProduct[productName] = { count: 0, totalPrice: 0 };
      }
      salesByProduct[productName].count += 1;
      salesByProduct[productName].totalPrice += parseFloat(sale.price) || 0;
    });
    return salesByProduct;
  };

  // Count sales by company and prepare data
  const salesByProduct = countSalesByProduct();
  const salesByProductData = {
    labels: Object.keys(salesByProduct),
    datasets: [
      {
        label: "Sales by Product",
        data: Object.values(salesByProduct).map((productName) => productName.count),
        backgroundColor: Array.from({ length: 50 }, (_, index) => `hsl(${index * 100}, 50%, 50%)`)
      }
    ]
  };
  // Custom tooltip for sales by company to show both total price and count
  const productOptions = {
    plugins: {
      datalabels: {
        display: true,
        align: "end",
        anchor: "end",
        color: "black",
        formatter: (value, context) => {
          return `${value}%`; // Adjust label formatting if needed
        }
      },
      legend: {
        position: "right"
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const productName = tooltipItem.label;
            const data = salesByProduct[productName];
            const totalPrice = data?.totalPrice || 0; // Default to 0 if totalPrice is not defined
            return [
              `Total Price: $${typeof totalPrice === "number" ? totalPrice.toFixed(2) : totalPrice}`,
              `Count: ${data?.count}`
            ];
          }
        }
      }
    }
  };

  const formatHeader = (prefix) => {
    // console.log("APPLIED FILTERS", appliedFilter);
    const { createdOnUTC } = appliedFilter;

    if (!createdOnUTC) {
      return `${prefix}`;
    }

    const formatHeaderDateLocal = (d) =>
      d.toLocaleDateString("en-AU", {
        timeZone: targetTimeZone,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

    const start = createdOnUTC["$gte"] ? new Date(createdOnUTC["$gte"]) : null;
    const end = createdOnUTC["$lte"] ? new Date(createdOnUTC["$lte"]) : null;

    if (start && end) return `${prefix} from ${formatHeaderDateLocal(start)} - ${formatHeaderDateLocal(end)}`;
    if (start) return `${prefix} from ${formatHeaderDateLocal(start)}`;
    if (end) return `${prefix} up to ${formatHeaderDateLocal(end)}`;
    return `${prefix}`;
  };

  const filteredData = useMemo(() => {
    return salesData.filter((transaction) => {
      const customerName = transaction.customer
        ? `${transaction.customer?.firstName} ${transaction.customer?.lastName}`.toLowerCase()
        : "";
      const productName = transaction.product ? transaction.product?.name.toLowerCase() : "";
      const partnerCompanyName = transaction.partnerCompany ? transaction.partnerCompany?.name.toLowerCase() : "";
      const partnerUserName = transaction.partnerUser
        ? `${transaction.partnerUser?.firstName} ${transaction.partnerUser?.lastName}`.toLowerCase()
        : "";
      const invoiceID = transaction.invoiceID ? transaction.invoiceID?.toLowerCase() : "";

      return (
        customerName.includes(searchQuery.toLowerCase()) ||
        productName.includes(searchQuery.toLowerCase()) ||
        partnerCompanyName.includes(searchQuery.toLowerCase()) ||
        partnerUserName.includes(searchQuery.toLowerCase()) ||
        invoiceID.includes(searchQuery.toLowerCase())
      );
    });
  }, [salesData, searchQuery]);

  // Paginate the filtered data
  useEffect(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setPaginatedData(filteredData.slice(startIndex, endIndex));
  }, [filteredData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const handleRowClick = (transactionId) => {
    const url = `/transaction/${transactionId}`;
    window.open(url, "_blank");
  };

  // Debugging information
  // console.log('Sales Data:', salesData);
  // console.log('Sales by Company Data:', salesByCompanyData);
  // console.log('Total Sales by Month Data:', totalSalesByMonthData);
  // console.log('Sales by Product Data:', salesByProductData);

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center gap-3 py-4 text-sm text-gray-600" role="status">
          <PuffLoader color="purple" size={36} />
          Loading transactions…
        </div>
      )}

      <div className="flex flex-wrap justify-center">
        {salesData.length > 0 ? (
          <div className="w-full flex justify-center items-center flex-wrap gap-2">
            {/* Summary Statistics */}
            <div className="w-full flex justify-center gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center p-4 bg-white rounded shadow">
                <div className="text-2xl font-bold text-blue-600">{totalCount.toLocaleString()}</div>
                <div className="text-sm text-gray-600 mt-1">Total Transactions</div>
              </div>
              <div className="text-center p-4 bg-white rounded shadow">
                <div className="text-2xl font-bold text-green-600">
                  ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-sm text-gray-600 mt-1">Total Value</div>
              </div>
            </div>
            <div className="w-full max-w-[800px]">
              <h2>{formatHeader("Sales by Partner Company")}</h2>
              <div className="w-full max-w-screen-md">
                <Bar data={salesByCompanyData} options={companyOptions} />
              </div>
            </div>
            <div className="w-full max-w-[800px]">
              <h2>{formatHeader("Subscription Creations per Day (New)")}</h2>
              <div className="w-full max-w-screen-md">
                <Line data={newTotalSalesByDayData} options={newDayOptions} />
              </div>
            </div>
            <div className="w-full max-w-[800px]">
              <h2>{formatHeader("Renewals per Day (Recurring)")}</h2>
              <div className="w-full max-w-screen-md">
                <Line data={renewalTotalSalesByDayData} options={renewalDayOptions} />
              </div>
            </div>
            <div className="w-full max-w-[800px]">
              <h2>{formatHeader("Sales by Products")}</h2>
              <div className="w-full max-w-screen-md">
                <Pie data={salesByProductData} options={productOptions} />
              </div>
            </div>
            <div className="w-full transactionRecords">
              <div className="mb-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder="Search transactions by customer, product, partner company, or invoice ID..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="overflow-x-auto shadow-md rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Partner Company Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Submitted By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.map((transaction, index) => {
                      const customerInfo = transaction.customer ? (
                        <div className="flex flex-column flex-wrap max-w-lg">
                          {transaction.customer.firstName} {transaction.customer.lastName}
                          <br />
                          {transaction.customer.email}
                          <br />
                          {transaction.customer.phone}
                        </div>
                      ) : (
                        ""
                      );
                      const productName = transaction.product?.name;
                      const partnerCompanyName = transaction.partnerCompany?.name;
                      const partnerUserName = transaction.partnerUser
                        ? `${transaction.partnerUser?.firstName} ${transaction.partnerUser?.lastName}`
                        : "";
                      const invoiceStatus = transaction.invoiceStatus;
                      const createdOnUTC = new Date(transaction.createdOnUTC).toLocaleDateString("en-AU", {
                        timeZone: targetTimeZone,
                      });
                      const invoicePrice = transaction.price;
                      const rowNumber = (currentPage - 1) * pageSize + index + 1;

                      return (
                        <tr
                          key={transaction._id}
                          className="cursor-pointer text-left hover:bg-gray-50"
                          onClick={() => handleRowClick(transaction._id)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-lg">{rowNumber}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-lg">{productName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-lg">{customerInfo}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-lg">{createdOnUTC}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-lg">
                            {partnerCompanyName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-lg">
                            {partnerUserName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-lg">
                            {invoiceStatus}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-lg">
                            ${invoicePrice ?? 0}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4 p-4 bg-gray-50 rounded">
                  <div className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * pageSize + 1} to{" "}
                    {Math.min(currentPage * pageSize, filteredData.length)} of {filteredData.length} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded ${
                        currentPage === 1
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-blue-500 text-white hover:bg-blue-600"
                      }`}
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded ${
                        currentPage === totalPages
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-blue-500 text-white hover:bg-blue-600"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          !loading && <p className="text-center text-gray-500 py-8">No sales data available</p>
        )}
      </div>
    </>
  );
};

export default TransactionSalesCharts;
